import React from 'react';
import type { ESignPackage, Participant, Timestamp } from '../../types/docmagic';
import { formatDate, formatTime } from '../../utils/datetime';
import { STAGES, isBorrowerRole, requiredSigners, stageIndex } from '../../utils/packageStatus';
interface ParticipantsTableProps {
  pkg: ESignPackage;
}
const timestampColumns: {
  key: keyof Participant;
  label: string;
  tone?: string;
}[] = [{
  key: 'created',
  label: 'Created'
}, {
  key: 'started',
  label: 'Started'
}, {
  key: 'consented',
  label: 'Consented'
}, {
  key: 'viewed',
  label: 'Viewed'
}, {
  key: 'completed',
  label: 'Completed',
  tone: 'text-success font-semibold'
}, {
  key: 'declined',
  label: 'Declined',
  tone: 'text-error font-semibold'
}];
const roleStyles: Record<string, string> = {
  Borrower: 'bg-[#eef4ff] text-[#0b5cab]',
  'Co-Borrower': 'bg-[#eef4ff] text-[#0b5cab]',
  Originator: 'bg-canvas text-muted'
};
export function ParticipantsTable({
  pkg
}: ParticipantsTableProps) {
  const required = requiredSigners(pkg);
  const done = required.filter(p => p.completed).length;
  const pct = required.length ? Math.round(done / required.length * 100) : 0;
  return <section aria-labelledby="participants-heading" data-id="element-6" data-mp="1wi6wtttjhcmn">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-7" data-mp="2bdmvyd8z7ypw">
        <div data-id="element-8" data-mp="26ea6rlumufk5">
          <h3 id="participants-heading" className="text-sm font-bold text-ink" data-id="element-9" data-mp="26dqscf46y9av">
            Participants ({pkg.participants.length})
          </h3>
          <p className="mt-0.5 text-xs text-muted" data-id="element-10" data-mp="1ioz2vvhzk51s">
            {pkg.requireOriginatorCompletion ? 'Borrowers and originator must complete before the signed PDF is retrieved.' : 'Signed PDF is retrieved when all borrowers complete. Originator status is not required.'}
          </p>
        </div>
        <div className="w-full sm:w-64" data-id="element-11" data-mp="1nlxzqlt1aq3o">
          <div className="flex justify-between text-xs" data-id="element-12" data-mp="zx6bu6mh8twp">
            <span className="text-muted" data-id="element-13" data-mp="uz4t5msyqwlr">Required signers</span>
            <span className="font-semibold text-ink" data-id="element-14" data-mp="uyljsuhdpgjc">
              {done} of {required.length} completed
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Required signer completion" data-id="element-15" data-mp="1ooudwphhghmm">
            <div className="h-full rounded-full bg-success transition-all" style={{
            width: `${pct}%`
          }} data-id="element-16" data-mp="1ygrpvygbfdme" />
          </div>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded border border-line" data-id="element-17" data-mp="15p83k5of9jg6">
        <table className="w-full min-w-[1080px] border-collapse text-sm" data-id="element-18" data-mp="1ap2ug9876918">
          <thead data-id="element-19" data-mp="1ar5t1hnf1ahk">
            <tr className="bg-canvas text-left text-xs font-semibold text-muted" data-id="element-20" data-mp="1fo5zhto6gf97">
              <th scope="col" className="w-10 px-3 py-2" data-id="element-21" data-mp="1fm30wl8yldsv">#</th>
              <th scope="col" className="px-3 py-2" data-id="element-22" data-mp="2m3gdn0s0a0ax">Name / Email</th>
              <th scope="col" className="px-3 py-2" data-id="element-23" data-mp="2r2tbshobutst">Role</th>
              {timestampColumns.map(c => <th key={c.key} scope="col" className="px-3 py-2" data-id="element-24" data-mp="fgiu1q857ub8">{c.label}</th>)}
              <th scope="col" className="px-3 py-2 text-right" data-id="element-25" data-mp="agcxbvn0nhma">Reference ID</th>
            </tr>
          </thead>
          <tbody data-id="element-26" data-mp="5p4l6ruaxi5f">
            {pkg.participants.map(p => {
            const stage = stageIndex(p);
            const isRequired = isBorrowerRole(p) || pkg.requireOriginatorCompletion && p.role === 'Originator';
            return <tr key={p.referenceId} className="border-t border-line align-top hover:bg-[#fafafa]" data-id="element-27" data-mp="3sssyay5rhi4u">
                  <td className="px-3 py-3 text-muted" data-id="element-28" data-mp="3ny5f9etj1fxn">{p.sequence}</td>
                  <td className="px-3 py-3" data-id="element-29" data-mp="3ix7xh3cfinwo">
                    <p className="font-semibold text-ink" data-id="element-30" data-mp="3dxv3lyn54gkl">{p.name}</p>
                    <p className="text-xs text-muted" data-id="element-31" data-mp="3dzpqhihz2irb">{p.email}</p>
                    <div className="mt-1.5 flex gap-1" aria-label={`Progress: ${stage >= 0 ? STAGES[stage] : 'not started'}`} data-id="element-32" data-mp="1h41pf8z24dxd">
                      {STAGES.map((s, i) => <span key={s} className={`h-1 w-5 rounded-full ${p.declined ? 'bg-error' : i <= stage ? stage === 4 ? 'bg-success' : 'bg-brand' : 'bg-line'}`} data-id="element-33" data-mp="1k6oaou5n7qjl" data-mpi={i} />)}
                    </div>
                  </td>
                  <td className="px-3 py-3" data-id="element-34" data-mp="11gge6uwg7ycd">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${roleStyles[p.role] ?? 'bg-canvas text-muted'}`} data-id="element-35" data-mp="11fog9dfdpoix">
                      {p.role}
                    </span>
                    {isRequired && <p className="mt-1 text-[11px] text-muted" data-id="element-36" data-mp="rk3k3vwzrih3">Required signer</p>}
                  </td>
                  {timestampColumns.map(c => <td key={c.key} className="whitespace-nowrap px-3 py-3" data-id="element-37" data-mp="ih2gc1vlm3gl">
                      <TimestampCell value={p[c.key] as Timestamp} tone={c.tone} data-id="element-38" data-mp="mj615h45qkej" />
                    </td>)}
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-xs text-muted" data-id="element-39" data-mp="3u3q2a8h9f3g">{p.referenceId}</td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </section>;
}
function TimestampCell({
  value,
  tone
}: {
  value: Timestamp;
  tone?: string;
}) {
  if (!value) return <span className="sr-only" data-id="element-40" data-mp="7uxer5tquxxe">Not recorded</span>;
  return <time dateTime={value} className={`block leading-tight ${tone ?? 'text-ink'}`} data-id="element-41" data-mp="mt7yphw3x6ul">
      <span className="block" data-id="element-42" data-mp="huwk6tuva861">{formatDate(value)}</span>
      <span className="block text-xs font-normal text-muted" data-id="element-43" data-mp="hueg5b1yqpnk">{formatTime(value)}</span>
    </time>;
}